import React, { useState, useEffect } from 'react';
import { FaCheck, FaTimes, FaCog, FaArrowLeft } from 'react-icons/fa';
import { auth, db } from '../../firebase/firebaseConfig';
import { doc, setDoc } from 'firebase/firestore';
import { createCheckoutSession } from '../../stripe/createCheckoutSession.ts';
import { usePremium } from '../../contexts/PremiumContext';
import { formatPartnerDisplayLabel } from '../../utils/partnerAccessExpiry';
import './SubscriptionPage.css';

/**
 * SubscriptionPage Component
 *
 * Displays plans (Free vs Pro) with a monthly/annual toggle and lets users
 * subscribe or manage an existing Flash Code Pro subscription.
 */
const SubscriptionPage = () => {
  const [currentView, setCurrentView] = useState('main');
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(() => auth.currentUser);
  const {
    isPremium,
    hasPaidSubscription,
    partnerProUntil,
    partnerCode,
    partnerName,
    planType: userPlanType,
    subscriptionData: premiumSubscriptionData,
  } = usePremium();
  const [subscriptionData, setSubscriptionData] = useState(premiumSubscriptionData);

  const hasPartnerAccess = Boolean(partnerProUntil) && !hasPaidSubscription;
  const partnerDisplayLabel = formatPartnerDisplayLabel(partnerName, partnerCode);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setSubscriptionData(premiumSubscriptionData);
  }, [premiumSubscriptionData]);

  useEffect(() => {
    if (userPlanType) {
      setSelectedPlan(userPlanType);
    }
  }, [userPlanType]);

  // Pricing plans (existing data)
  const plans = {
    monthly: {
      name: 'Monthly',
      price: '7.99',
      priceId: 'price_1Tvmh6H9z0ThRFdKBMzLb7U2',
      billingCycle: 'Billed monthly'
    },
    annual: {
      name: 'Annual',
      price: '5.58',
      priceId: 'price_1TvmkOH9z0ThRFdK3lOpZODp',
      billingCycle: 'Billed annually',
      annualTotal: '67.00',
      discount: 'Save 30%'
    }
  };

  // Pro feature set (shared between free user comparison and pro card)
  const proFeatures = [
    'Unlimited snippets & cookbooks',
    'Every published learning track & roadmap',
    'Byte: AI explanations, tutoring & snippet generation',
    'Priority support',
    'Early access to new features'
  ];

  // Free tier features (included)
  const freeIncluded = [
    'Interactive study modes & spaced repetition',
    'Up to 10 new snippets / month (manual adds)',
    'Up to 2 new cookbooks / month',
  ];

  // Free tier limitations (Pro-only features shown as X)
  const freeLimitations = [
    'Byte AI tutor & generation',
    'All published roadmaps & tracks',
  ];

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
  };

  const handleSubscribe = async () => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    setIsLoading(true);
    try {
      const priceId = plans[selectedPlan].priceId;
      await createCheckoutSession(user.uid, priceId);
    } catch (error) {
      console.error('Error creating checkout session:', error);
      const message =
        (error && typeof error === 'object' && 'message' in error && String(error.message)) ||
        'Could not open Stripe checkout. Please try again or contact support.';
      window.alert(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!user || !subscriptionData.id) {
      console.error('User not authenticated or subscription not found');
      return;
    }

    const confirmCancel = window.confirm(
      "Are you sure you want to end your subscription? You will still have access until the end of your current billing period."
    );

    if (!confirmCancel) return;

    setIsLoading(true);
    try {
      const cancelRef = doc(db, `users/${user.uid}/subscriptions/${subscriptionData.id}/cancelSubscription/cancel`);
      await setDoc(cancelRef, {
        timestamp: new Date().toISOString(),
        cancelAtPeriodEnd: true
      });

      setSubscriptionData({ ...subscriptionData, cancelAtPeriodEnd: true });
      alert('Your subscription has been set to cancel at the end of the current billing period.');
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert('There was an error canceling your subscription. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResumeSubscription = async () => {
    if (!user || !subscriptionData.id) {
      console.error('User not authenticated or subscription not found');
      return;
    }

    setIsLoading(true);
    try {
      const resumeRef = doc(db, `users/${user.uid}/subscriptions/${subscriptionData.id}/resumeSubscription/resume`);
      await setDoc(resumeRef, { timestamp: new Date().toISOString() });

      setSubscriptionData({ ...subscriptionData, cancelAtPeriodEnd: false });
      alert('Your subscription has been resumed and will automatically renew.');
    } catch (error) {
      console.error('Error resuming subscription:', error);
      alert('There was an error resuming your subscription. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwitchToAnnual = async () => {
    if (!user || !subscriptionData.id) {
      console.error('User not authenticated or subscription not found');
      return;
    }

    const confirmSwitch = window.confirm(
      "Are you sure you want to switch to the annual plan? Your current monthly subscription will be canceled, and you will be charged for the annual plan. Any unused portion of your current month will be credited toward your annual subscription."
    );

    if (!confirmSwitch) return;

    setIsLoading(true);
    try {
      const upgradeIntentRef = doc(db, `users/${user.uid}/subscription_upgrades/${subscriptionData.id}`);
      await setDoc(upgradeIntentRef, {
        originalSubscriptionId: subscriptionData.id,
        targetPlan: 'annual',
        targetPriceId: plans.annual.priceId,
        createdAt: new Date().toISOString(),
        status: 'pending',
        metadata: { from_plan: 'monthly', to_plan: 'annual' }
      });

      await createCheckoutSession(user.uid, plans.annual.priceId);
    } catch (error) {
      console.error('Error during subscription upgrade process:', error);
      alert('There was an error upgrading your subscription. Please try again.');
      setIsLoading(false);
    }
  };

  const formattedRenewalDate = subscriptionData.currentPeriodEnd
    ? new Date(subscriptionData.currentPeriodEnd).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    })
    : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });

  const formattedPartnerEndDate = partnerProUntil
    ? partnerProUntil.toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    })
    : null;

  // Active price shown on the Pro card based on the toggle
  const activePrice = selectedPlan === 'annual' ? plans.annual.price : plans.monthly.price;
  const activePeriodLabel = selectedPlan === 'annual' ? '/ month, billed annually' : '/ month';

  // Bottom footer note — varies by user state
  const renderFooterNote = () => {
    if (hasPartnerAccess) {
      return `Your ${partnerDisplayLabel} access is active until ${formattedPartnerEndDate}. Subscribe anytime to keep Pro after it ends.`;
    }
    if (hasPaidSubscription) {
      if (subscriptionData.cancelAtPeriodEnd) {
        return `Your subscription is set to cancel. You will have access until ${formattedRenewalDate}.`;
      }
      return `Your subscription renews automatically on ${formattedRenewalDate}. Manage it anytime in settings.`;
    }
    if (selectedPlan === 'annual') {
      return `Billed as $${plans.annual.annualTotal} / year. You save vs monthly.`;
    }
    return 'Your subscription renews automatically. Manage it anytime in settings.';
  };

  // Pro card CTA depends on premium status + selected plan
  const renderProCta = () => {
    if (hasPartnerAccess) {
      return (
        <button
          className="subscription-page__btn subscription-page__btn--primary"
          onClick={handleSubscribe}
          disabled={isLoading}
        >
          {isLoading ? 'processing…' : 'subscribe to keep pro'}
        </button>
      );
    }

    if (hasPaidSubscription) {
      if (userPlanType === selectedPlan) {
        return (
          <button className="subscription-page__btn subscription-page__btn--current" disabled>
            current plan
          </button>
        );
      }
      if (userPlanType === 'monthly' && selectedPlan === 'annual' && !subscriptionData.cancelAtPeriodEnd) {
        return (
          <button
            className="subscription-page__btn subscription-page__btn--primary"
            onClick={handleSwitchToAnnual}
            disabled={isLoading}
          >
            {isLoading ? 'processing…' : 'switch to annual'}
          </button>
        );
      }
      return (
        <button className="subscription-page__btn subscription-page__btn--current" disabled>
          current plan
        </button>
      );
    }

    return (
      <button
        className="subscription-page__btn subscription-page__btn--primary"
        onClick={handleSubscribe}
        disabled={isLoading}
      >
        {isLoading ? 'processing…' : 'switch to pro'}
      </button>
    );
  };

  // Free card CTA
  const renderFreeCta = () => {
    if (hasPaidSubscription) {
      return (
        <button className="subscription-page__btn" disabled>
          downgrade in settings
        </button>
      );
    }
    if (hasPartnerAccess || isPremium) {
      return (
        <button className="subscription-page__btn" disabled>
          not included
        </button>
      );
    }
    return (
      <button className="subscription-page__btn" disabled>
        current plan
      </button>
    );
  };

  // ---- Management view (cancel / resume) -------------------------------
  if (hasPaidSubscription && currentView === 'management') {
    return (
      <div className="subscription-page">
        <div className="subscription-page__container">
          <div className="subscription-page__management-header">
            <button
              className="subscription-page__back-button"
              onClick={() => setCurrentView('main')}
              aria-label="Back to plans"
            >
              <FaArrowLeft />
            </button>
            <p className="subscription-page__section-label">// manage subscription</p>
          </div>

          <div className="subscription-page__management-card">
            <div className="subscription-page__management-row">
              <p className="subscription-page__management-label">Current Plan</p>
              <p className="subscription-page__management-value">
                {userPlanType === 'annual' ? 'Annual' : 'Monthly'}, ${plans[userPlanType || 'monthly'].price}/mo
              </p>
            </div>

            <div className="subscription-page__management-divider" />

            <div className="subscription-page__management-row">
              <p className="subscription-page__management-label">
                {subscriptionData.cancelAtPeriodEnd ? 'Access Until' : 'Next Renewal'}
              </p>
              <p className="subscription-page__management-value">{formattedRenewalDate}</p>
              <p className="subscription-page__management-hint">
                {subscriptionData.cancelAtPeriodEnd
                  ? 'Your subscription will not renew after this date.'
                  : 'Your subscription will automatically renew on this date.'}
              </p>
            </div>
          </div>

          <div className="subscription-page__management-actions">
            {!subscriptionData.cancelAtPeriodEnd ? (
              <button
                className="subscription-page__btn subscription-page__btn--danger"
                onClick={handleCancelSubscription}
                disabled={isLoading}
              >
                {isLoading ? 'processing…' : 'end subscription'}
              </button>
            ) : (
              <button
                className="subscription-page__btn subscription-page__btn--primary"
                onClick={handleResumeSubscription}
                disabled={isLoading}
              >
                {isLoading ? 'processing…' : 'resume subscription'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---- Main view -------------------------------------------------------
  return (
    <div className="subscription-page">
      <div className="subscription-page__container">
        {hasPaidSubscription && (
          <button
            className="subscription-page__settings-button"
            onClick={() => setCurrentView('management')}
            title="Manage subscription"
            aria-label="Manage subscription"
          >
            <FaCog />
          </button>
        )}

        <div className="subscription-page__intro">
          <p className="subscription-page__section-label">// pricing</p>
          <h2 className="subscription-page__title">
            {isPremium ? 'Flash Code Pro' : 'Level up your skills'}
          </h2>
          <p className="subscription-page__subtitle">
            {hasPartnerAccess
              ? `${partnerDisplayLabel} access active until ${formattedPartnerEndDate}. All Pro features are unlocked.`
              : hasPaidSubscription
                ? `You're on the ${userPlanType === 'annual' ? 'annual' : 'monthly'} plan.`
                : 'Choose your plan and start training.'}
          </p>
        </div>

        <div className="subscription-page__toggle" role="tablist" aria-label="Billing cycle">
          <button
            type="button"
            role="tab"
            aria-selected={selectedPlan === 'monthly'}
            className={`subscription-page__toggle-tab${selectedPlan === 'monthly' ? ' subscription-page__toggle-tab--active' : ''}`}
            onClick={() => handleSelectPlan('monthly')}
          >
            monthly
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={selectedPlan === 'annual'}
            className={`subscription-page__toggle-tab${selectedPlan === 'annual' ? ' subscription-page__toggle-tab--active' : ''}`}
            onClick={() => handleSelectPlan('annual')}
          >
            annual <span className="subscription-page__save-pill">{plans.annual.discount}</span>
          </button>
        </div>

        <div className="subscription-page__grid">
          {/* Free card */}
          <div className="subscription-page__card">
            <p className="subscription-page__section-label">// free tier</p>
            <p className="subscription-page__plan-name">Free</p>
            <p className="subscription-page__plan-sub">Start building the habit.</p>

            <div className="subscription-page__price-row">
              <span className="subscription-page__free-label">$0</span>
              <span className="subscription-page__price-period">/ forever</span>
            </div>

            {freeIncluded.map((feature, i) => (
              <div className="subscription-page__feature" key={`free-${i}`}>
                <FaCheck className="subscription-page__check subscription-page__check--muted" aria-hidden="true" />
                <span>{feature}</span>
              </div>
            ))}
            {freeLimitations.map((feature, i) => (
              <div
                className="subscription-page__feature subscription-page__feature--disabled"
                key={`free-x-${i}`}
              >
                <FaTimes className="subscription-page__cross" aria-hidden="true" />
                <span>{feature}</span>
              </div>
            ))}

            {renderFreeCta()}
          </div>

          {/* Pro card */}
          <div className="subscription-page__card subscription-page__card--featured">
            <span className="subscription-page__badge">✦ PRO</span>
            <p className="subscription-page__plan-name">Pro</p>
            <p className="subscription-page__plan-sub">Your full-stack training gym.</p>

            <div className="subscription-page__price-row">
              {selectedPlan === 'annual' && (
                <span className="subscription-page__price-approx" aria-label="approximately">~</span>
              )}
              <span className="subscription-page__price-dollar">$</span>
              <span className="subscription-page__price-amount">{activePrice}</span>
              <span className="subscription-page__price-period">{activePeriodLabel}</span>
            </div>

            {proFeatures.map((feature, i) => (
              <div className="subscription-page__feature" key={`pro-${i}`}>
                <FaCheck className="subscription-page__check" aria-hidden="true" />
                <span>{feature}</span>
              </div>
            ))}

            {renderProCta()}
          </div>
        </div>

        <p className="subscription-page__annual-note">{renderFooterNote()}</p>
      </div>
    </div>
  );
};

export default SubscriptionPage;
